import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BirthDateInput from '../../components/ui/BirthDateInput';
import { useAuthStore } from '../../stores/auth.store';
import { authApi } from '../../api/auth.api';
import { getAccessToken } from '../../api/client';
import { CountrySelect } from '../../components/ui/CountrySelect';
import { ShieldCheck, KeyRound, Download, Trash2, Icon, LogOut } from '../../components/ui/Icon';
import TwoFactorModal from '../../components/auth/TwoFactorModal';
import ChangePasswordModal from '../../components/auth/ChangePasswordModal';
import { emailLocale } from '../../i18n';
import { toast } from '../../components/ui/Toast';
import { format } from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';
import Section from '../../components/ui/Section';
import PlanSection from '../../components/billing/PlanSection';
import TravelStatsSection from '../../components/stats/TravelStatsSection';
import AvatarEditor from '../../components/account/AvatarEditor';
import StorageSection from '../../components/account/StorageSection';

function Row({ icon, label, value, onClick, danger }: {
  icon: React.ElementType;
  label: string;
  value?: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  const content = (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${onClick ? 'cursor-pointer' : ''} ${danger ? 'hover:bg-red-50 dark:hover:bg-red-900/10' : onClick ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50' : ''} transition-colors`}>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${danger ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
        <Icon as={icon} size={15} className={danger ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${danger ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>{label}</p>
        {value && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">{value}</p>}
      </div>
    </div>
  );

  if (onClick) return <button className="w-full text-left" onClick={onClick}>{content}</button>;
  return <div>{content}</div>;
}

export default function AccountPage() {
  const { user, setAuth, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingNat, setSavingNat] = useState(false);

  // Google-only account: there is no current password to check against, so the
  // first one is set through the emailed link rather than from a live session.
  async function handleSendSetPasswordLink() {
    if (!user?.email) return;
    try {
      await authApi.forgotPassword({ email: user.email, locale: emailLocale() });
      toast(t('account.changePassword.setLinkSent'), 'success');
    } catch {
      toast(t('account.changePassword.setLinkFailed'), 'error');
    }
  }

  async function handleNationalityChange(code: string) {
    if (code === user?.nationalityCode) return;
    setSavingNat(true);
    try {
      const updated = await authApi.updateMe({ nationalityCode: code });
      const token = getAccessToken();
      if (token) setAuth(updated, token);
    } catch {
      toast(t('account.nationalitySaveFailed'), 'error');
    } finally {
      setSavingNat(false);
    }
  }

  // ADR-0029: their own date, so they see it whole; trips only ever see the age.
  async function handleBirthDateChange(birthDate: string) {
    if (!birthDate || birthDate === user?.birthDate) return;
    try {
      const updated = await authApi.updateMe({ birthDate });
      const token = getAccessToken();
      if (token) setAuth(updated, token);
      toast(t('account.birthDateSaved'), 'success');
    } catch {
      toast(t('account.birthDateSaveFailed'), 'error');
    }
  }

  async function handleExportData() {
    try {
      const blob = await authApi.exportMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gromly-my-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast(t('account.exportFailed'), 'error');
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await authApi.deleteAccount();
      clearAuth();
      navigate('/login');
    } catch {
      toast(t('account.deleteFailed'), 'error');
      setDeleting(false);
    }
  }

  async function handleLogout() {
    await authApi.logout().catch(() => null);
    clearAuth();
    navigate('/login');
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-50">{t('account.title')}</h1>
        <p className="text-sm text-slate-400 dark:text-slate-500 mt-0.5">{t('account.subtitle')}</p>
      </div>

      {/* Profile */}
      <Section title={t('account.profile')}>
        <div className="flex items-center gap-4 px-4 py-4">
          <AvatarEditor />
          <div className="min-w-0">
            <p className="text-base font-semibold text-slate-900 dark:text-slate-50 truncate">
              {user?.displayName || user?.username}
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 truncate">@{user?.username}</p>
            <p className="text-sm text-slate-400 dark:text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-slate-600 dark:text-slate-400 shrink-0">{t('account.nationality')}</span>
          <div className="w-44">
            <CountrySelect value={user?.nationalityCode ?? ''} onChange={handleNationalityChange} />
          </div>
        </div>
        {savingNat && <div className="px-4 -mt-2 pb-2 text-[11px] text-slate-400">{t('common.saving')}</div>}
        <div className="px-4 py-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label htmlFor="account-birth-date" className="text-sm text-slate-600 dark:text-slate-400 shrink-0">{t('account.birthDate')}</label>
            <BirthDateInput id="account-birth-date" className="w-full sm:w-80" value={user?.birthDate ?? ''} onChange={handleBirthDateChange} />
          </div>
          <p className="text-[12px] text-slate-400 dark:text-slate-500">{t('account.birthDateHint')}</p>
        </div>
        {user?.createdAt && (
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-slate-400 dark:text-slate-500">{t('account.memberSince')}</span>
            <span className="text-xs text-slate-600 dark:text-slate-400">
              {format(new Date(user.createdAt), 'MMMM yyyy', { locale: dateLocale })}
            </span>
          </div>
        )}
      </Section>

      <TravelStatsSection />

      <PlanSection />

      {/* Credits */}
      <Section title={t('account.credits')}>
        <div className="px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-slate-600 dark:text-slate-400">{t('account.tripCredits')}</span>
          <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">{user?.isAdmin ? '∞' : (user?.tripCredits ?? 0)}</span>
        </div>
        <div className="px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-slate-600 dark:text-slate-400">{t('account.aiCredits')}</span>
          <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">{user?.isAdmin ? '∞' : (user?.aiCredits ?? 0)}</span>
        </div>
      </Section>

      <StorageSection />

      {/* Security */}
      <Section title={t('account.security')}>
        <Row
          icon={KeyRound}
          label={user?.hasPassword ? t('account.changePassword.title') : t('account.changePassword.setTitle')}
          value={user?.hasPassword ? t('account.changePassword.rowHint') : t('account.changePassword.setHint')}
          onClick={user?.hasPassword ? () => setPasswordOpen(true) : handleSendSetPasswordLink}
        />
        <Row
          icon={ShieldCheck}
          label={t('account.twoFactor')}
          value={user?.twoFactorEnabled ? t('account.enabled') : t('account.notEnabled')}
          onClick={() => setTwoFAOpen(true)}
        />
      </Section>

      {/* Data */}
      <Section title={t('account.data')}>
        <Row
          icon={Download}
          label={t('account.exportData')}
          value={t('account.exportDataDesc')}
          onClick={handleExportData}
        />
      </Section>

      {/* Danger zone */}
      <Section title={t('account.danger')}>
        <Row
          icon={LogOut}
          label={t('account.signOut')}
          onClick={handleLogout}
        />
        <Row
          icon={Trash2}
          label={t('account.deleteAccount')}
          value={t('account.deleteAccountDesc')}
          onClick={() => setDeleteConfirm(true)}
          danger
        />
      </Section>

      {twoFAOpen && <TwoFactorModal onClose={() => setTwoFAOpen(false)} />}

      {passwordOpen && <ChangePasswordModal onClose={() => setPasswordOpen(false)} />}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                <Icon as={Trash2} size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">{t('account.deleteAccount')}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('account.deleteConfirmSubtitle')}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('account.deleteConfirmBody')}
            </p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60"
              >
                {deleting ? t('common.deleting') : t('account.deletePermanently')}
              </button>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
              {t('account.saveFirst')}{' '}
              <button
                onClick={() => { setDeleteConfirm(false); handleExportData(); }}
                className="underline hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer"
              >
                {t('account.exportData')}
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
